import { useCallback, useContext, useEffect, useSyncExternalStore } from "react";
import { useMemorySliceSelector } from "../shared";
import { useLayoutRef } from "../shared/hooks/useLayoutRef";
import { PersistenceContext } from "./PersistenceProvider";
import { persistenceMemoryStore } from "./runtime/persistenceMemoryStore";
import { persistentSliceStore } from "./runtime/PersistentSliceStore";
import type { PersistedSliceDescriptor, PersistedSliceTuple, PersistenceBootStatus } from "./types";

/**
 * Returns persistence boot status from the root provider.
 *
 * @returns `{ status: "booting" | "ready" }`.
 * @throws When used outside `PersistenceProvider`.
 */
export const usePersistenceStatus = (): { "status": PersistenceBootStatus } => {
    const context = useContext( PersistenceContext );
    if ( context == null ) {
        throw new Error( "usePersistenceStatus must be used within PersistenceProvider" );
    }

    return context;
};

/**
 * Subscribes to a persisted slice with fine-grained updates.
 *
 * @typeParam TValue Slice value type from the descriptor schema.
 * @param slice Slice descriptor (stable reference from slice module).
 * @returns Tuple `[value, setValue]` compatible with `useState` updaters.
 */
export const usePersistedSlice = <TValue>(
    slice: PersistedSliceDescriptor<TValue>,
): PersistedSliceTuple<TValue> => {
    useEffect( () => {
        void persistentSliceStore.ensureHydrated( slice );
    }, [ slice ] );

    const sliceRef = useLayoutRef( slice );

    const subscribe = useCallback( ( listener: () => void ) => {
        return persistentSliceStore.subscribe( sliceRef.current, listener );
    }, [ sliceRef ] );

    const getSnapshot = useCallback( () => {
        return persistentSliceStore.getSnapshot( sliceRef.current );
    }, [ sliceRef ] );

    const value = useSyncExternalStore( subscribe, getSnapshot, getSnapshot );

    const setValue = useCallback(
        ( next: TValue | ( ( previous: TValue ) => TValue ) ) => {
            persistentSliceStore.setValue( slice, next );
        },
        [ slice ],
    );

    return [ value, setValue ];
};

/**
 * Subscribes to a derived portion of a slice to limit re-renders.
 *
 * @typeParam TValue Full slice value type.
 * @typeParam TSelected Projected value type.
 * @param slice Slice descriptor.
 * @param selector Projection from full slice value; should be stable (`useCallback`) when non-trivial.
 * @param isEqual Optional equality for selected values (defaults to `Object.is`).
 * @returns Selected value; re-renders only when the selection changes per `isEqual`.
 */
export const usePersistedSliceSelector = <TValue, TSelected>(
    slice: PersistedSliceDescriptor<TValue>,
    selector: ( value: TValue ) => TSelected,
    isEqual?: ( left: TSelected, right: TSelected ) => boolean,
): TSelected => {
    useEffect( () => {
        void persistentSliceStore.ensureHydrated( slice );
    }, [ slice ] );

    return useMemorySliceSelector( persistenceMemoryStore, slice, selector, isEqual );
};

/**
 * Returns whether persistence eager hydration has completed.
 *
 * @returns `true` when `PersistenceProvider` status is `ready`.
 * @remarks Legacy alias for components that previously gated on `StoredContext.isLoaded`.
 */
export const usePersistenceReady = (): boolean => {
    const { status } = usePersistenceStatus();

    return status === "ready";
};
